import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { formatCurrency, formatDate, getContractTypeBadge, parseContracts } from '../MessageBubbleHelpers';

import { HOVER_CARD_SX } from './sharedCardHelpers';

type ContractDetailItem = { label: string; value: string };
type ParsedContract = NonNullable<ReturnType<typeof parseContracts>>['contracts'][number];

// When the payload's statuses are indeterminate (e.g. all "Unknown"), the parser emits "-"
// rather than a number. Preserve that sentinel so the chip shows "-" instead of a misleading 0.
const isUnknownCount = (value: string) => (value || '').trim() === '-';
const isNumericLike = (value: string) => /^-?\$?[\d,.]+$/.test((value || '').trim());
// Percentage-like fields carry "payment" in the label but represent a percent, not money.
const isPercentageDetailLabel = (label: string) => /percentage|percent|\bpct\b/i.test(label || '');
// Count-like fields (term, payment counts) carry "payment"/"term" in the label but are
// integers, not money — they must NOT be currency-formatted.
const isCountDetailLabel = (label: string) =>
    /(number of payments|payments done|payments made|payments received|payments completed|remaining payments|total payments|\bterm\b|\bcount\b)/i.test(
        label || '',
    );
// A label is a real date when "date" is the trailing noun ("Last Payment Date",
// "Start Date", "Maturity Date", "Oldest Open Invoice Date") — NOT when "date" only
// appears mid-phrase as in "Year To Date Interest"/"Year To Date Principal", which are
// money fields. Anchoring on a trailing "date" keeps "Last Payment Date" out of the
// currency bucket (it contains "payment") and out of raw passthrough.
const isDateDetailLabel = (label: string) => /\bdate\s*$/i.test((label || '').trim());
const isCurrencyDetailLabel = (label: string) =>
    !isDateDetailLabel(label) &&
    !isCountDetailLabel(label) &&
    !isPercentageDetailLabel(label) &&
    /(amount|balance|principal|interest|payment|past due|due)/i.test(label || '');

const hasMeaningfulValue = (value: string) => {
    const normalized = (value || '').trim();
    const lowered = normalized.toLowerCase();
    return Boolean(
        normalized && normalized !== '-' && lowered !== 'n/a' && lowered !== 'null' && lowered !== 'undefined',
    );
};

const formatYieldDetailValue = (label: string, value: string) => {
    if (!/yield/i.test(label) || !/^-?\d+(?:\.\d+)?$/.test(value)) {
        return undefined;
    }
    const numericYield = Number(value);
    return Number.isFinite(numericYield) ? `${numericYield.toFixed(2)}%` : undefined;
};

const formatPercentageDetailValue = (label: string, value: string) => {
    if (!isPercentageDetailLabel(label) || !/^-?\$?\d[\d,]*(?:\.\d+)?$/.test(value)) {
        return undefined;
    }
    const numericPercent = Number(value.replace(/[$,]/g, ''));
    return Number.isFinite(numericPercent) ? `${numericPercent.toFixed(2)}%` : undefined;
};

const formatAdditionalDetailValue = (label: string, value: string) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue || normalizedValue === '-') {
        return '-';
    }

    if (/^n\/?a$/i.test(normalizedValue)) {
        return 'N/A';
    }

    if (/frequency/i.test(label) && /^m$/i.test(normalizedValue)) {
        return 'Monthly';
    }

    const yieldValue = formatYieldDetailValue(label, normalizedValue);
    if (yieldValue) {
        return yieldValue;
    }

    const percentageValue = formatPercentageDetailValue(label, normalizedValue);
    if (percentageValue) {
        return percentageValue;
    }

    if (isDateDetailLabel(label)) {
        return formatDate(normalizedValue);
    }

    if (isCurrencyDetailLabel(label) && isNumericLike(normalizedValue)) {
        return formatCurrency(normalizedValue);
    }

    return normalizedValue;
};

const getContractTypeBadgeColors = (type: string) => {
    const badge = getContractTypeBadge(type).toLowerCase();
    if (badge === 'loan') return { backgroundColor: 'rgba(146, 170, 235, 0.12)', color: '#496ac6' };
    if (badge === 'lease') return { backgroundColor: 'rgba(117, 117, 255, 0.12)', color: '#9649e2' };
    return { backgroundColor: 'rgba(89, 131, 245, 0.12)', color: '#5983f5' };
};

const LEASE_ONLY_FIELDS = [
    'start date',
    'maturity date',
    'term end date',
    'next payment amount',
    'billing frequency',
    'past due',
    'lease value',
    'last payment made',
];

const DEFAULT_PREFERRED_ORDER = [
    'monthly',
    'next due',
    'principal',
    'last payment',
    'progress',
    'billing frequency',
    'remaining principal',
    'yield',
    'start date',
    'maturity date',
    'next payment amount',
    'last payment amount',
    'past due',
    'life to date principal',
    'total amount paid',
    'term',
    'life to date interest',
    'total interest paid',
    'year to date interest',
    'year to date principal',
    'prior year interest',
    'prior year principal',
];

const normalizeLabel = (label: string) =>
    label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const getPreferredOrderIndex = (normalizedLabel: string, preferredOrder: string[]) => {
    const exactIndex = preferredOrder.findIndex(key => key === normalizedLabel);
    if (exactIndex !== -1) {
        return exactIndex;
    }
    return preferredOrder.findIndex(key => normalizedLabel.includes(key) || key.includes(normalizedLabel));
};

const isLeaseAllowedDetail = (normalizedItemLabel: string) => {
    if (normalizedItemLabel === 'term') {
        return false;
    }
    return LEASE_ONLY_FIELDS.some(
        field => normalizedItemLabel.includes(field.replace(/\s+/g, ' ')) || field.includes(normalizedItemLabel),
    );
};

const filterDetailItemsForCard = (items: ContractDetailItem[], isLeaseDetailsCard: boolean) =>
    items.filter(item => {
        if (!hasMeaningfulValue(item.value)) return false;
        if (!isLeaseDetailsCard) return true;
        return isLeaseAllowedDetail(normalizeLabel(item.label));
    });

const sortDetailItems = (items: ContractDetailItem[], preferredOrder: string[]) =>
    [...items].sort((a, b) => {
        const aIndex = getPreferredOrderIndex(normalizeLabel(a.label), preferredOrder);
        const bIndex = getPreferredOrderIndex(normalizeLabel(b.label), preferredOrder);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return normalizeLabel(a.label).localeCompare(normalizeLabel(b.label));
    });

const getDisplayDetailLabel = (label: string, isLeaseDetailsCard: boolean) => {
    const normalized = normalizeLabel(label);
    if (isLeaseDetailsCard && normalized === 'maturity date') {
        return 'Term End Date';
    }
    if (normalized.includes('life to date principal') && normalized.includes('total amount paid')) {
        return 'Life-to-Date Principal';
    }
    if (normalized.includes('life to date interest') && normalized.includes('total interest paid')) {
        return 'Life-to-Date Interest';
    }
    return label;
};

const buildOrderedDetailItems = (detailItems: ContractDetailItem[], isLeaseDetailsCard: boolean) => {
    const preferredOrder = isLeaseDetailsCard ? LEASE_ONLY_FIELDS : DEFAULT_PREFERRED_ORDER;
    const filteredItems = filterDetailItemsForCard(detailItems, isLeaseDetailsCard);
    return sortDetailItems(filteredItems, preferredOrder);
};

const buildPrimaryDetailItems = (
    visibleFields: Array<{ label: string; value: string; asCurrency: boolean }>,
    isLeaseDetailsCard: boolean,
) =>
    visibleFields.map(field => {
        const rawValue = (field.value || '').trim();
        const isCurrencyLike = /^\$?[\d,.]+$/.test(rawValue);
        const formattedValue = field.asCurrency && isCurrencyLike ? formatCurrency(rawValue) : rawValue;

        let label: string = field.label;
        if (isLeaseDetailsCard && field.label === 'PRINCIPAL') {
            label = 'Lease Value';
        } else if (isLeaseDetailsCard && field.label === 'LAST PAYMENT') {
            label = 'Last Payment Made';
        }

        return { label, value: formattedValue };
    });

const buildExtraDetailItems = (formattedAdditionalDetails: ContractDetailItem[], isLeaseDetailsCard: boolean) =>
    formattedAdditionalDetails
        .filter(detail => hasMeaningfulValue(detail.value))
        .map(detail => ({
            label:
                isLeaseDetailsCard && /^\s*principal\s*amount\s*$/i.test(detail.label) ? 'Lease Value' : detail.label,
            value: detail.value,
        }));

const ProgressOnlyDetail: React.FC<{ contractId: string; progressValue: string }> = ({ contractId, progressValue }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1,
            py: 0.9,
            backgroundColor: colors.greyPreset[5],
            borderRadius: '8px',
            border: `1px solid ${colors.greyPreset[20]}`,
        }}
    >
        <Box sx={{ minWidth: 0 }}>
            <Typography
                variant="caption"
                sx={{
                    color: colors.greyPreset[50],
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                }}
            >
                Contract ID
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '1rem', lineHeight: 1.3, mt: 0.2 }}
            >
                #{contractId}
            </Typography>
        </Box>

        <Box sx={{ minWidth: 0, textAlign: 'right' }}>
            <Typography
                variant="caption"
                sx={{
                    color: colors.greyPreset[50],
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                }}
            >
                Progress
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '1rem', lineHeight: 1.3, mt: 0.2 }}
            >
                {progressValue}
            </Typography>
        </Box>
    </Box>
);

const DetailGrid: React.FC<{ contractId: string; items: ContractDetailItem[]; isLeaseDetailsCard: boolean }> = ({
    contractId,
    items,
    isLeaseDetailsCard,
}) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: 0.9,
            rowGap: 1.7,
            p: 1,
            backgroundColor: colors.greyPreset[5],
            borderRadius: '8px',
            border: `1px solid ${colors.greyPreset[20]}`,
        }}
    >
        {items.map(item => (
            <Box key={`${contractId}-${item.label}`} sx={{ minWidth: 0, pr: 0.4 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: colors.greyPreset[50],
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                    }}
                >
                    {getDisplayDetailLabel(item.label, isLeaseDetailsCard)}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: colors.fullBlack,
                        fontWeight: 700,
                        fontSize: '1rem',
                        lineHeight: 1.35,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        mt: 0.2,
                    }}
                >
                    {item.value}
                </Typography>
            </Box>
        ))}
    </Box>
);

const ContractDetailsBody: React.FC<{
    contract: ParsedContract;
    visibleFields: Array<{ label: string; value: string; asCurrency: boolean }>;
    hasProgressOnly: boolean;
    formattedAdditionalDetails: ContractDetailItem[];
    isContractDetailsResponse: boolean;
}> = ({ contract, visibleFields, hasProgressOnly, formattedAdditionalDetails, isContractDetailsResponse }) => {
    if (hasProgressOnly) {
        return <ProgressOnlyDetail contractId={contract.contractId} progressValue={visibleFields[0].value} />;
    }

    const isLease = (contract.type || '').toLowerCase().includes('lease');
    const isLeaseDetailsCard = isLease && isContractDetailsResponse;

    const primaryItems = buildPrimaryDetailItems(visibleFields, isLeaseDetailsCard);
    const extraItems = buildExtraDetailItems(formattedAdditionalDetails, isLeaseDetailsCard);
    const orderedDetailItems = buildOrderedDetailItems([...primaryItems, ...extraItems], isLeaseDetailsCard);

    if (!orderedDetailItems.length) {
        return null;
    }

    return (
        <DetailGrid
            contractId={contract.contractId}
            items={orderedDetailItems}
            isLeaseDetailsCard={isLeaseDetailsCard}
        />
    );
};

const ContractTypeBadge: React.FC<{ type: string }> = ({ type }) => {
    const badgeColors = getContractTypeBadgeColors(type);
    return (
        <Typography
            variant="caption"
            sx={{
                px: 0.75,
                py: 0.35,
                borderRadius: '6px',
                backgroundColor: badgeColors.backgroundColor,
                color: badgeColors.color,
                fontWeight: 700,
                textTransform: 'capitalize',
                letterSpacing: 0.5,
            }}
        >
            {getContractTypeBadge(type)}
        </Typography>
    );
};

const ContractStatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const isClosed = status.toLowerCase().includes('closed');
    return (
        <Typography
            variant="caption"
            sx={{
                px: 0.75,
                py: 0.35,
                borderRadius: '6px',
                backgroundColor: isClosed ? 'rgba(225, 231, 235, 0.8)' : 'rgba(10, 142, 122, 0.12)',
                color: isClosed ? '#6B7280' : '#087262',
                fontWeight: 700,
                textTransform: 'capitalize',
                letterSpacing: 0.5,
            }}
        >
            {status}
        </Typography>
    );
};

const ContractHeader: React.FC<{ contract: ParsedContract }> = ({ contract }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
        <Stack direction="column" gap={0.15}>
            <Typography
                variant="caption"
                sx={{
                    color: colors.greyPreset[50],
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                }}
            >
                Contract ID
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}
            >
                #{contract.contractId}
            </Typography>
        </Stack>
        <Stack direction="row" gap={0.6}>
            {contract.type && <ContractTypeBadge type={contract.type} />}
            {contract.status && <ContractStatusBadge status={contract.status} />}
        </Stack>
    </Stack>
);

const ContractCardItem: React.FC<{
    contract: ParsedContract;
    isDateIntentQuery: boolean;
    isContractDetailsResponse: boolean;
}> = ({ contract, isDateIntentQuery, isContractDetailsResponse }) => {
    const fullFieldConfig = [
        { label: 'MONTHLY', value: contract.monthlyPayment, asCurrency: true },
        { label: 'NEXT DUE', value: contract.nextPaymentOn, asCurrency: false },
        { label: 'PRINCIPAL', value: contract.principalAmount, asCurrency: true },
        { label: 'LAST PAYMENT', value: contract.lastPayment, asCurrency: true },
        { label: 'PROGRESS', value: contract.progress, asCurrency: false },
    ];

    // Date-intent query (e.g. "when does contract start and end?"): show only the
    // date fields. Every other query keeps the full card.
    const fieldConfig = isDateIntentQuery
        ? fullFieldConfig.filter(field => /date|due/i.test(field.label))
        : fullFieldConfig;

    const visibleFields = fieldConfig.filter(field => hasMeaningfulValue(field.value));
    const hasProgressOnly = visibleFields.length === 1 && visibleFields[0].label === 'PROGRESS';
    const dateOnlyAdditionalDetails = isDateIntentQuery
        ? (contract.additionalDetails || []).filter(detail => isDateDetailLabel(detail.label))
        : contract.additionalDetails || [];
    const formattedAdditionalDetails = dateOnlyAdditionalDetails.map(detail => ({
        ...detail,
        value: formatAdditionalDetailValue(detail.label, detail.value),
    }));

    return (
        <Box sx={HOVER_CARD_SX}>
            <Box sx={{ p: 1.5 }}>
                {!hasProgressOnly && <ContractHeader contract={contract} />}

                {!hasProgressOnly && contract.description && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: colors.greyPreset[60],
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            mb: 1,
                            lineHeight: 1.4,
                        }}
                    >
                        {contract.description}
                    </Typography>
                )}

                <ContractDetailsBody
                    contract={contract}
                    visibleFields={visibleFields}
                    hasProgressOnly={hasProgressOnly}
                    formattedAdditionalDetails={formattedAdditionalDetails}
                    isContractDetailsResponse={isContractDetailsResponse}
                />
            </Box>
        </Box>
    );
};

export const ContractsCard: React.FC<{
    contracts: ReturnType<typeof parseContracts>;
    isDateIntentQuery: boolean;
}> = ({ contracts, isDateIntentQuery }) => {
    if (!contracts) return null;

    const statusCountUnknown = isUnknownCount(contracts.activeCount) || isUnknownCount(contracts.closedCount);
    const activeCount = Number(contracts.activeCount) || 0;
    const closedCount = Number(contracts.closedCount) || 0;

    const statusCountItems: Array<{ label: 'Active' | 'Closed'; count: number }> = [];
    const isContractDetailsResponse = /^here are the details for/i.test(contracts.intro || '');

    // Only show the Active/Closed chips when the response actually carries recognizable
    // contract statuses. For asset/details/other responses where status is indeterminate, hide
    // the chips entirely rather than showing a meaningless "Active: - | Closed: -".
    if (!statusCountUnknown) {
        if (activeCount > 0) {
            statusCountItems.push({ label: 'Active', count: activeCount });
        }
        if (closedCount > 0) {
            statusCountItems.push({ label: 'Closed', count: closedCount });
        }
    }

    const sortedContracts = [...contracts.contracts].sort((a, b) => {
        const aIsClosed = (a.status || '').toLowerCase().includes('closed');
        const bIsClosed = (b.status || '').toLowerCase().includes('closed');
        if (aIsClosed === bIsClosed) return 0;
        return aIsClosed ? 1 : -1;
    });

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.2, pb: 1, borderBottom: `1px solid ${colors.greyPreset[20]}` }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: colors.greyPreset[50],
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                    }}
                >
                    Contracts Overview
                </Typography>
                {statusCountItems.length > 0 && (
                    <Typography
                        variant="body2"
                        sx={{ color: colors.greyPreset[60], fontWeight: 600, fontSize: '0.85rem' }}
                    >
                        {statusCountItems.map((item, index) => (
                            <React.Fragment key={item.label}>
                                {index > 0 && ' | '}
                                {item.label}:{' '}
                                <span style={{ color: colors.fullBlack, fontWeight: 700 }}>{item.count}</span>
                            </React.Fragment>
                        ))}
                    </Typography>
                )}
            </Stack>

            {contracts.intro && (
                <Typography
                    variant="body2"
                    sx={{
                        mb: 1,
                        color: colors.greyPreset[60],
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        lineHeight: 1.5,
                    }}
                >
                    {contracts.intro}
                </Typography>
            )}

            <Stack gap={1}>
                {sortedContracts.map(contract => (
                    <ContractCardItem
                        key={contract.contractId}
                        contract={contract}
                        isDateIntentQuery={isDateIntentQuery}
                        isContractDetailsResponse={isContractDetailsResponse}
                    />
                ))}
            </Stack>

            {contracts.followUp && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 1.5,
                        color: colors.greyPreset[60],
                        whiteSpace: 'pre-line',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                    }}
                >
                    {contracts.followUp}
                </Typography>
            )}
        </Box>
    );
};
